import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Person, 
  MedicalServices, 
  School, 
  ExpandMore, 
  Add, 
  Delete, 
  ContentCopy,
  HelpOutline
} from '@mui/icons-material';
import * as etapaTratamientoService from '../../services/etapaTratamientoService';
import especialidadService from '../../services/especialidadService';

// Categorías de variables predefinidas
const VARIABLE_CATEGORIES = [
  {
    id: 'sistema',
    name: 'Sistema',
    icon: <School />,
    variables: [
      { name: 'fecha_actual', description: 'Fecha actual en formato DD/MM/YYYY' },
      { name: 'hora_actual', description: 'Hora actual en formato HH:MM' },
      { name: 'nombre_clinica', description: 'Nombre de la clínica' },
      { name: 'direccion_clinica', description: 'Dirección de la clínica' },
      { name: 'telefono_clinica', description: 'Teléfono de la clínica' }
    ]
  },
  {
    id: 'paciente',
    name: 'Paciente',
    icon: <Person />,
    variables: [
      { name: 'paciente.nombre', description: 'Nombre del paciente' },
      { name: 'paciente.apellidos', description: 'Apellidos del paciente' },
      { name: 'paciente.nombre_completo', description: 'Nombre completo del paciente' },
      { name: 'paciente.dni', description: 'DNI del paciente' },
      { name: 'paciente.edad', description: 'Edad del paciente' },
      { name: 'paciente.fecha_nacimiento', description: 'Fecha de nacimiento del paciente' },
      { name: 'paciente.telefono', description: 'Teléfono del paciente' },
      { name: 'paciente.email', description: 'Email del paciente' },
      { name: 'paciente.direccion', description: 'Dirección del paciente' },
      { name: 'paciente.numero_historia', description: 'Número de historia clínica' },
      { name: 'paciente.sexo', description: 'Sexo del paciente' }
    ]
  },
  {
    id: 'especialista',
    name: 'Especialista',
    icon: <MedicalServices />,
    variables: [
      { name: 'especialista.nombre', description: 'Nombre del especialista' },
      { name: 'especialista.apellidos', description: 'Apellidos del especialista' },
      { name: 'especialista.nombre_completo', description: 'Nombre completo del especialista' },
      { name: 'especialista.num_colegiatura', description: 'Número de colegiatura' },
      { name: 'especialista.especialidad', description: 'Especialidad del profesional' }
    ]
  },
  {
    id: 'historia',
    name: 'Historia Clínica',
    icon: <MedicalServices />,
    variables: [
      { name: 'historia.fecha_creacion', description: 'Fecha de creación de la historia' },
      { name: 'historia.motivo_consulta', description: 'Motivo de consulta' },
      { name: 'historia.diagnostico', description: 'Diagnóstico principal' },
      { name: 'historia.diagnostico_cie10', description: 'Código CIE-10 del diagnóstico' },
      { name: 'historia.plan_tratamiento', description: 'Plan de tratamiento' },
      { name: 'historia.especialidad', description: 'Especialidad de la historia' }
    ]
  }
];

const VariablesEtapaSelector = ({ variables = [], onChange, plantilla }) => {
  const [selectedVariables, setSelectedVariables] = useState(variables || []);
  const [customVariables, setCustomVariables] = useState([]);
  const [newVariableName, setNewVariableName] = useState('');
  const [newVariableDescription, setNewVariableDescription] = useState('');
  const [etapas, setEtapas] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [etapasResponse, especialidadesResponse] = await Promise.all([
          etapaTratamientoService.getAllEtapas(),
          especialidadService.getEspecialidades()
        ]);
                
        setEtapas(etapasResponse.data);
        setEspecialidades(especialidadesResponse.data);
        
        // Extraer variables personalizadas del array de variables
        const systemVarNames = VARIABLE_CATEGORIES.flatMap(
          category => category.variables.map(v => v.name)
        );
        
        const customVars = variables.filter(
          v => !systemVarNames.includes(v.name)
        );
        
        setCustomVariables(customVars);
      } catch (error) {
        console.error('Error al cargar datos:', error);
      }
    };
    
    fetchData();
  }, [variables]);

  const handleVariableToggle = (variable) => {
    const exists = selectedVariables.some(v => v.name === variable.name);
    
    if (exists) {
      // Eliminar variable
      const updatedVariables = selectedVariables.filter(v => v.name !== variable.name);
      setSelectedVariables(updatedVariables);
      onChange(updatedVariables);
    } else {
      // Agregar variable
      const updatedVariables = [...selectedVariables, variable];
      setSelectedVariables(updatedVariables);
      onChange(updatedVariables);
    }
  };

  const handleAddCustomVariable = () => {
    if (!newVariableName) return;
    
    const newVariable = {
      name: newVariableName,
      description: newVariableDescription || newVariableName,
      custom: true
    };
    
    const updatedCustomVariables = [...customVariables, newVariable];
    setCustomVariables(updatedCustomVariables);
    
    // También agregar a las variables seleccionadas
    const updatedSelectedVariables = [...selectedVariables, newVariable];
    setSelectedVariables(updatedSelectedVariables);
    onChange(updatedSelectedVariables);
    
    // Limpiar campos
    setNewVariableName('');
    setNewVariableDescription('');
  };

  const handleRemoveCustomVariable = (variable) => {
    // Eliminar de variables personalizadas
    const updatedCustomVariables = customVariables.filter(v => v.name !== variable.name);
    setCustomVariables(updatedCustomVariables);
    
    // Eliminar de variables seleccionadas
    const updatedSelectedVariables = selectedVariables.filter(v => v.name !== variable.name);
    setSelectedVariables(updatedSelectedVariables);
    onChange(updatedSelectedVariables);
  };

  const isVariableSelected = (variableName) => {
    return selectedVariables.some(v => v.name === variableName);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(`{{${text}}}`);
  };

  // Variables específicas por etapa
  const getEtapaVariables = () => {
    if (!plantilla?.etapa_id) return [];
    
    const etapa = etapas.find(e => e.id === parseInt(plantilla.etapa_id));
    if (!etapa) return [];
    
    // Construir variables específicas de la etapa
    return [
      { name: `etapa.nombre`, description: 'Nombre de la etapa de tratamiento' },
      { name: `etapa.duracion_estimada`, description: 'Duración estimada de la etapa (en sesiones)' },
      { name: `etapa.descripcion`, description: 'Descripción de la etapa' }
    ];
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom>
            Variables disponibles
          </Typography>
          
          {/* Categorías de variables del sistema */}
          {VARIABLE_CATEGORIES.map((category) => (
            <Accordion key={category.id} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center">
                  <Box mr={1}>{category.icon}</Box>
                  <Typography variant="subtitle1">{category.name}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {category.variables.map((variable) => (
                    <ListItem 
                      key={variable.name}
                      button
                      onClick={() => handleVariableToggle(variable)}
                      selected={isVariableSelected(variable.name)}
                    >
                      <ListItemText 
                        primary={variable.name} 
                        secondary={variable.description} 
                      />
                      <Tooltip title="Copiar variable">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(variable.name);
                          }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
          
          {/* Variables específicas de la etapa seleccionada */}
          {plantilla?.etapa_id && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center">
                  <Box mr={1}><School /></Box>
                  <Typography variant="subtitle1">Variables de Etapa</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {getEtapaVariables().map((variable) => (
                    <ListItem 
                      key={variable.name}
                      button
                      onClick={() => handleVariableToggle(variable)}
                      selected={isVariableSelected(variable.name)}
                    >
                      <ListItemText 
                        primary={variable.name} 
                        secondary={variable.description} 
                      />
                      <Tooltip title="Copiar variable">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(variable.name);
                          }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
          
          {/* Variables personalizadas */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center">
                <Box mr={1}><Add /></Box>
                <Typography variant="subtitle1">Variables Personalizadas</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={5}>
                  <TextField 
                    label="Nombre de la variable"
                    value={newVariableName}
                    onChange={(e) => setNewVariableName(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField 
                    label="Descripción"
                    value={newVariableDescription}
                    onChange={(e) => setNewVariableDescription(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handleAddCustomVariable}
                    fullWidth
                    disabled={!newVariableName}
                  >
                    Agregar
                  </Button>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              {customVariables.length > 0 ? (
                <List dense>
                  {customVariables.map((variable) => (
                    <ListItem 
                      key={variable.name}
                      button
                      onClick={() => handleVariableToggle(variable)}
                      selected={isVariableSelected(variable.name)}
                      secondaryAction={
                        <IconButton 
                          edge="end" 
                          color="error"
                          onClick={() => handleRemoveCustomVariable(variable)}
                        >
                          <Delete />
                        </IconButton>
                      }
                    >
                      <ListItemText 
                        primary={variable.name} 
                        secondary={variable.description} 
                      />
                      <Tooltip title="Copiar variable">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(variable.name);
                          }}
                          sx={{ mr: 2 }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="textSecondary" align="center" py={2}>
                  No hay variables personalizadas
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Typography variant="h6" component="div" flexGrow={1}>
                Variables seleccionadas
              </Typography>
              <Tooltip title="Usar las variables en el documento encerradas entre dobles llaves: {{variable}}">
                <IconButton size="small">
                  <HelpOutline />
                </IconButton>
              </Tooltip>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            {selectedVariables.length > 0 ? (
              <Box>
                {selectedVariables.map((variable) => (
                  <Chip
                    key={variable.name}
                    label={variable.name}
                    onDelete={() => handleVariableToggle(variable)}
                    color="primary"
                    variant="outlined"
                    sx={{ m: 0.5 }}
                    onClick={() => copyToClipboard(variable.name)}
                  />
                ))}
              </Box>
            ) : (
              <Typography color="textSecondary" align="center" py={2}>
                Seleccione variables para usar en la plantilla
              </Typography>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" gutterBottom>
              Cómo usar las variables:
            </Typography>
            <Typography variant="body2" paragraph>
              Para incluir una variable en la plantilla, escriba el nombre de la variable 
              entre dobles llaves, por ejemplo: <strong>{"{{paciente.nombre}}"}</strong>
            </Typography>
            <Typography variant="body2">
              Puede hacer clic en una variable para copiarla al portapapeles.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VariablesEtapaSelector;